import { Agent, ComAtprotoRepoGetRecord } from '@atproto/api'
import type { AtpAgent } from '@atproto/api'
import type { OAuthSession } from '@atproto/oauth-client-browser'
import type { ProfileData } from '@/pages/Profile/Profile.types'
import { getATProfileGraphQL, getAPProfileGraphQL } from '@/lib/atExplore'
import type { ProfileWithBridgeStatus } from '@/types/graphql'

export interface EmbeddedProfile {
    did: string
    handle: string
    displayName?: string
    avatar?: string
}

/**
 * Batch-fetch minimal profile data for a list of identifiers (DIDs or handles).
 * Makes a single getProfiles call (capped at 25). Mirrors fetchEmbeddedPosts.
 */
export async function fetchEmbeddedProfiles(
    identifiers: string[],
    agent: AtpAgent
): Promise<EmbeddedProfile[]> {
    if (identifiers.length === 0) return []
    const capped = identifiers.slice(0, 25)
    const response = await agent.app.bsky.actor.getProfiles({ actors: capped })
    return response.data.profiles.map(p => ({
        did: p.did,
        handle: p.handle,
        displayName: p.displayName,
        avatar: p.avatar,
    }))
}

export interface ProfileBasic {
    did: string
    handle?: string
    displayName?: string
    avatar?: string
    isVerified: boolean
}

/**
 * Batch-fetch basic profile data (avatar, isVerified) for a list of DIDs.
 * Chunks requests into groups of 25 (Bluesky API limit for getProfiles).
 * Returns a Map<DID, ProfileBasic>; individual chunk failures are silently skipped.
 */
export async function fetchProfilesBatch(
    dids: string[],
    agent: AtpAgent
): Promise<Map<string, ProfileBasic>> {
    const result = new Map<string, ProfileBasic>()
    if (dids.length === 0) return result

    const chunks: string[][] = []
    for (let i = 0; i < dids.length; i += 25) {
        chunks.push(dids.slice(i, i + 25))
    }

    const responses = await Promise.allSettled(
        chunks.map(chunk => agent.app.bsky.actor.getProfiles({ actors: chunk }))
    )

    for (const res of responses) {
        if (res.status !== 'fulfilled') continue
        for (const profile of res.value.data.profiles) {
            result.set(profile.did, {
                did: profile.did,
                handle: profile.handle,
                displayName: profile.displayName,
                avatar: profile.avatar,
                // `verification` is not yet typed in @atproto/api; cast until the SDK exposes it
                isVerified:
                    (profile.verification as { verifiedStatus?: string } | undefined)
                        ?.verifiedStatus === 'valid',
            })
        }
    }

    return result
}

type GraphQLLinkedAccount = NonNullable<ProfileWithBridgeStatus['linkedAccounts']>[number]

function mapGraphQLLinkedAccount(account: GraphQLLinkedAccount) {
    return {
        id: account.rkey,
        rkey: account.rkey,
        icon: account.icon,
        name: account.name,
        link: account.link,
        order: account.order,
        ...(account.verifiedAt ? { verifiedAt: account.verifiedAt } : {}),
        ...(account.source ? { source: account.source } : {}),
        ...(account.iconUrl ? { iconUrl: account.iconUrl } : {}),
        ...(account.uri ? { uri: account.uri } : {}),
        ...(account.cid ? { cid: account.cid } : {}),
    }
}

/**
 * Fetch Bluesky profile data via GraphQL (all data in single query)
 */
export async function fetchBlueskyProfile(
    did: string,
    options?: { publicAgent?: Agent }
): Promise<ProfileData> {
    // GraphQL now returns everything: profile, stats, verification, extended bio, linked accounts
    const graphqlProfile = await getATProfileGraphQL(did, options)

    return {
        did: graphqlProfile.did,
        handle: graphqlProfile.handle,
        displayName: graphqlProfile.displayName,
        description: graphqlProfile.description,
        avatar: graphqlProfile.avatar,
        banner: graphqlProfile.banner,
        followersCount: graphqlProfile.followersCount,
        followsCount: graphqlProfile.followsCount,
        postsCount: graphqlProfile.postsCount,
        indexedAt: new Date().toISOString(),
        isVerified: graphqlProfile.isVerified,
        pronouns: graphqlProfile.pronouns,
        extendedBio: graphqlProfile.extendedBio,
        linkedAccounts: graphqlProfile.linkedAccounts?.map(mapGraphQLLinkedAccount),
        mackinacLinks: graphqlProfile.mackinacLinks?.map(mapGraphQLLinkedAccount),
        blueskyLinks: graphqlProfile.blueskyLinks?.map(mapGraphQLLinkedAccount),
        bridgeStatus: graphqlProfile.bridgeStatus,
        website: graphqlProfile.website,
        location: graphqlProfile.location,
        labels: graphqlProfile.labels,
        collections: graphqlProfile.collections,
    }
}

/**
 * Fetch Pixelfed profile data via GraphQL (same as Mastodon - both are ActivityPub)
 */
export async function fetchPixelfedProfile(
    instanceUrl: string,
    username: string
): Promise<ProfileData> {
    const domain = new URL(instanceUrl).host
    const handle = `${username}@${domain}`

    const graphqlProfile = await getAPProfileGraphQL(handle, { includeMackinacBio: false })

    return {
        did: graphqlProfile.did,
        handle: graphqlProfile.handle,
        displayName: graphqlProfile.displayName,
        description: graphqlProfile.description,
        avatar: graphqlProfile.avatar,
        banner: graphqlProfile.banner,
        followersCount: graphqlProfile.followersCount,
        followsCount: graphqlProfile.followsCount,
        postsCount: graphqlProfile.postsCount,
        isVerified: graphqlProfile.isVerified,
        linkedAccounts: graphqlProfile.linkedAccounts?.map(mapGraphQLLinkedAccount),
        rawMastodonFields: graphqlProfile.rawMastodonFields,
        bridgeStatus: graphqlProfile.bridgeStatus,
        extendedBio: graphqlProfile.extendedBio,
        website: graphqlProfile.website,
        location: graphqlProfile.location,
    }
}

/**
 * Fetch the logged-in user's own Mastodon profile.
 *
 * Runs `verify_credentials` (authenticated, bypasses server-side caching) and the
 * at-explore GraphQL query in parallel. The GraphQL result provides rich fields
 * (bridgeStatus, linkedAccounts, etc.) while `verify_credentials` provides the
 * authoritative, always-fresh avatar URL.
 *
 * Falls back to the at-explore avatar if `verify_credentials` fails.
 * Throws if the GraphQL call fails (same behaviour as fetchMastodonProfile).
 */
export async function fetchMastodonOwnProfile(
    instanceUrl: string,
    username: string,
    accessToken: string
): Promise<ProfileData> {
    const domain = new URL(instanceUrl).host
    const handle = `${username}@${domain}`

    const [graphqlResult, credentialsResult] = await Promise.allSettled([
        getAPProfileGraphQL(handle),
        fetch(`${instanceUrl}/api/v1/accounts/verify_credentials`, {
            headers: { Authorization: `Bearer ${accessToken}` },
        }).then(res => {
            if (!res.ok) throw new Error(`verify_credentials failed: ${res.status}`)
            return res.json() as Promise<{ avatar: string }>
        }),
    ])

    if (graphqlResult.status === 'rejected') {
        throw graphqlResult.reason
    }

    const graphqlProfile = graphqlResult.value
    const resolvedHandle =
        graphqlProfile.bridgeStatus?.sourceProtocol === 'atprotocol' &&
        graphqlProfile.bridgeStatus?.blueskyHandle
            ? graphqlProfile.bridgeStatus.blueskyHandle
            : graphqlProfile.handle

    const avatar =
        credentialsResult.status === 'fulfilled'
            ? credentialsResult.value.avatar
            : graphqlProfile.avatar

    return {
        did: graphqlProfile.did,
        handle: resolvedHandle,
        displayName: graphqlProfile.displayName,
        description: graphqlProfile.description,
        avatar,
        banner: graphqlProfile.banner,
        followersCount: graphqlProfile.followersCount,
        followsCount: graphqlProfile.followsCount,
        postsCount: graphqlProfile.postsCount,
        isVerified: graphqlProfile.isVerified,
        linkedAccounts: graphqlProfile.linkedAccounts?.map(mapGraphQLLinkedAccount),
        rawMastodonFields: graphqlProfile.rawMastodonFields,
        bridgeStatus: graphqlProfile.bridgeStatus,
        extendedBio: graphqlProfile.extendedBio,
        // getAPProfileGraphQL resolves this from the mackinac extended-bio
        // record, falling back to a native "Pronouns" profile field.
        pronouns: graphqlProfile.pronouns,
        website: graphqlProfile.website,
        location: graphqlProfile.location,
    }
}

/**
 * Fetch Mastodon profile data via GraphQL (includes verification from bridged AT Protocol account)
 */
export async function fetchMastodonProfile(
    instanceUrl: string,
    username: string
): Promise<ProfileData> {
    const domain = new URL(instanceUrl).host
    const handle = `${username}@${domain}`

    const graphqlProfile = await getAPProfileGraphQL(handle)

    // For AT→AP bridged users, use the clean Bluesky handle instead of the full AP handle
    const resolvedHandle =
        graphqlProfile.bridgeStatus?.sourceProtocol === 'atprotocol' &&
        graphqlProfile.bridgeStatus?.blueskyHandle
            ? graphqlProfile.bridgeStatus.blueskyHandle
            : graphqlProfile.handle

    return {
        did: graphqlProfile.did,
        handle: resolvedHandle,
        displayName: graphqlProfile.displayName,
        description: graphqlProfile.description,
        avatar: graphqlProfile.avatar,
        banner: graphqlProfile.banner,
        followersCount: graphqlProfile.followersCount,
        followsCount: graphqlProfile.followsCount,
        postsCount: graphqlProfile.postsCount,
        isVerified: graphqlProfile.isVerified,
        linkedAccounts: graphqlProfile.linkedAccounts?.map(mapGraphQLLinkedAccount),
        rawMastodonFields: graphqlProfile.rawMastodonFields,
        bridgeStatus: graphqlProfile.bridgeStatus,
    }
}

/** Timeout for outbound Bluesky PDS/AppView calls in the pin/unpin path. */
export const PINNED_POST_NETWORK_TIMEOUT_MS = 10_000

/**
 * Runs an `@atproto/api` Agent call with an `AbortSignal` that fires after
 * `PINNED_POST_NETWORK_TIMEOUT_MS`, so a hung PDS/AppView response fails after a
 * bounded time instead of blocking indefinitely. Mirrors the `AbortController` +
 * `FETCH_TIMEOUT_MS` pattern used for raw `fetch()` calls in mastodonService.ts —
 * but Agent methods take `signal` via their own `CallOptions` rather than a raw
 * `fetch()` call, so this wraps that mechanism instead of force-fitting one.
 */
async function withNetworkTimeout<T>(fn: (signal: AbortSignal) => Promise<T>): Promise<T> {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), PINNED_POST_NETWORK_TIMEOUT_MS)
    try {
        return await fn(controller.signal)
    } finally {
        clearTimeout(timeout)
    }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Minimal runtime check on the `pinnedPost` ref before it's trusted to drive the
 * stale-unpin guard below. The PDS-read profile record itself is intentionally
 * read/written verbatim without a full lexicon validator (see the comment on
 * `setBlueskyPinnedPost` for why), so this is the trust-boundary check for the
 * one field the control flow actually branches on.
 */
function isPinnedPostRef(value: unknown): value is { uri: string; cid?: string } {
    if (!isPlainObject(value)) return false
    return (
        typeof value.uri === 'string' && (value.cid === undefined || typeof value.cid === 'string')
    )
}

/**
 * An unpin that the PDS record contradicts: something other than the post the
 * user acted on is pinned right now (another device, another tab, or a list row
 * this client has not refetched yet), or the stored `pinnedPost` is present but
 * not a shape we can read.
 *
 * This is thrown rather than returned quietly because the caller's optimistic UI
 * has *already* cleared the pinned slot. Resolving successfully would tell the
 * user "Post unpinned" and leave an empty slot on screen while their profile
 * still shows a pin — a silent divergence with nothing to correct it. Throwing
 * routes it through the mutation's error path, which rolls the optimistic state
 * back and resyncs against the server, matching how the pin path already fails
 * closed via the `swapRecord` CAS.
 */
export class StalePinnedPostError extends Error {
    readonly expectedUri: string
    /** What the PDS actually has pinned, or undefined when it is unreadable. */
    readonly actualUri: string | undefined

    constructor(expectedUri: string, actualUri: string | undefined) {
        super(
            `[pinnedPost] refusing stale unpin: expected ${expectedUri} to be pinned, PDS has ${actualUri ?? 'an unreadable ref'}`
        )
        this.name = 'StalePinnedPostError'
        this.expectedUri = expectedUri
        this.actualUri = actualUri
    }
}

/**
 * Write (or clear) `pinnedPost` on the standard `app.bsky.actor.profile` record.
 *
 * Deliberately the *standard* profile record, not `social.mackinac.actor.profile`,
 * so other AT Protocol clients (bsky.app included) see the same pin. AT Protocol
 * supports exactly one pinned post — re-pinning replaces the previous ref.
 *
 * Preserves every other field on the record verbatim (see the note below on why
 * this does not go through `agent.upsertProfile`).
 */
export async function setBlueskyPinnedPost(
    session: OAuthSession,
    ref: { uri: string; cid: string } | null,
    /**
     * When clearing (`ref === null`), only clear if this URI is what is actually
     * pinned. Guards against a stale "Unpin" affordance wiping a pin the user set
     * afterwards (from another tab, device, or a not-yet-refetched list row).
     */
    expectedCurrentUri?: string
): Promise<void> {
    const agent = new Agent(session)
    const repo = session.did
    const collection = 'app.bsky.actor.profile'

    // Deliberately NOT agent.upsertProfile. That helper hands the updater
    // `undefined` when the stored record fails lexicon validation, but still
    // takes `swapRecord` from the record that *does* exist — so the CAS
    // succeeds and the write replaces the whole record. A profile that is
    // invalid under the current lexicon (an over-long description, a
    // `pinnedPost` another client wrote without a `cid`) would silently lose
    // displayName/description/avatar/banner and, more seriously, any
    // self-applied `labels` such as `!no-unauthenticated`. Pinning is a
    // one-tap action, so it must preserve the stored record verbatim.
    // Only a genuine "no record yet" (RecordNotFound) is safe to treat as an
    // empty profile. Any other getRecord failure (network blip, DPoP nonce
    // hiccup, 5xx) must propagate: on the unpin path in particular, silently
    // treating a failed read as "nothing pinned" would make the stale-unpin
    // guard below take its early return and report success while the PDS
    // still has the post pinned (the pin path stays fail-closed regardless,
    // via the swapRecord CAS on the write below).
    const existing = await withNetworkTimeout(signal =>
        agent.com.atproto.repo
            .getRecord({ repo, collection, rkey: 'self' }, { signal })
            .catch(err => {
                if (err instanceof ComAtprotoRepoGetRecord.RecordNotFoundError) return undefined
                throw err
            })
    )

    const rawValue = existing?.data.value
    if (rawValue !== undefined && !isPlainObject(rawValue)) {
        // A malformed read (not a plain object) can't be safely merged into via the
        // spread below — fail closed here, consistent with any other getRecord
        // failure above, rather than silently proceeding with garbage.
        throw new Error('[pinnedPost] PDS returned a malformed app.bsky.actor.profile record')
    }
    const stored = rawValue as Record<string, unknown> | undefined

    if (!ref && expectedCurrentUri !== undefined) {
        const pinnedPost = stored?.pinnedPost
        if (pinnedPost === undefined || pinnedPost === null) {
            // Nothing is pinned, so the state the user asked for already holds. The
            // write would be a no-op and the optimistic UI is already correct — this
            // is the one benign way the guard can trip, so it stays a quiet success.
            return
        }
        const currentUri = isPinnedPostRef(pinnedPost) ? pinnedPost.uri : undefined
        if (currentUri !== expectedCurrentUri) {
            // Something *else* is pinned now (or the ref is unreadable), so this unpin
            // is stale. Do not clear a pin the user did not ask to remove — and do not
            // report success either: the caller has already optimistically emptied the
            // pinned slot, and the profile still has one. Fail closed and let the
            // mutation roll back and resync. See `StalePinnedPostError`.
            console.warn(
                '[pinnedPost] refusing stale unpin; pinned post is no longer',
                expectedCurrentUri
            )
            throw new StalePinnedPostError(expectedCurrentUri, currentUri)
        }
    }

    const record: Record<string, unknown> = { ...(stored ?? {}), $type: collection }
    if (ref) {
        record.pinnedPost = ref
    } else {
        delete record.pinnedPost
    }

    await withNetworkTimeout(signal =>
        agent.com.atproto.repo.putRecord(
            {
                repo,
                collection,
                rkey: 'self',
                record,
                // Concurrency guard: match the record we read, or require that none exists.
                swapRecord: existing?.data.cid ?? null,
            },
            { signal }
        )
    )
}

// Commit confirmation for a pinned-post write lives in `@/services/pinnedPostCommit`.
// It used to poll `getProfile` here, through the user's PDS — a different transport
// (and a different cache policy) from the one the feed is read over, which is exactly
// why a confirmed pin could still render stale. See that module's header for detail.
